import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

import { FileVersionModel } from '../../../../../../../typings/api/skymap/rest/v1/.common';
import { CreateSubFolderFileParameters } from '../../../../../../../typings/api/skymap/rest/v1/folder';
import { CreateRootFolderFileParameters } from '../../../../../../../typings/api/skymap/rest/v1/project';
import { TokenService } from '../../../../../services/authentication/token.service';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { FileUpload } from '../../../../../services/network/file-upload';
import { getWebAPI } from '../../../../../services/web-api-client';
import { iiafe } from '../../../../../utils/async';
import * as fileVersionUtils from '../../../../../utils/models/file-version';
import { RequestParamsToApiPaths } from '../../../../../utils/typescript-utils';
import { handleError, TranslatedError } from '../../../../api/error-handling';
import { USE_NONCE_START_VALUE } from '../../../../hooks/use-nonce';
import { usePromisePool } from '../../../../hooks/use-promise-pool';
import { ProjectContext } from '../../../../state/project-state/project-state';
import { getNextFileVersionRevision } from '../../../../utils/file';
import { Table } from '../../../table/table';
import { FolderListViewContext } from '../../state/folder-list-view-state';
import { TreeContext } from '../../state/folder-tree-state';
import { UploadFileRow } from './upload-file-row';

export interface Props {
  startUploadNonce: number;
  cancelAllNonce: number;
  addFiles: File[];
  onSummaryChanged: (summary: Summary) => void;
  onClose?: () => void;
}

type ApiRequestPath = RequestParamsToApiPaths<
  CreateRootFolderFileParameters | CreateSubFolderFileParameters
>;

export enum UploadItemStatus {
  // Upload has not been started for file.
  Idle,

  // Pending upload.
  Pending,

  // Is currently beeing uploaded to S3.
  UploadingToS3,

  // Upload to S3 is done. Saving to database.
  SavingToDatabase,

  // Item successfully uploaded and saved to database.
  SavedToDatabase,

  // Upload or database save error, or something else (connection error or similar).
  Error,
}

export interface Summary {
  numErrors: number;
  numFiles: number;
  numUploaded: number;
  anyFilesInProgress: boolean;
}

export interface UploadItem {
  id: string;
  status: UploadItemStatus;
  errors?: TranslatedError<ApiRequestPath>[];
  showDescription: boolean;
  file: {
    isPreviouslyUploaded: boolean;
    htmlInputFile: File;
    uploadInstance: FileUpload;
    bytesUploaded: number;
    uploadId?: string;
  };
  meta: {
    revision?: string;
    revisionDate?: Date;
    scale?: string;
    description?: string;
  };
}

type UploadItemState = UploadItem[];
type UploadItemAction =
  | { type: 'ADD_FILES'; files: File[] }
  | { type: 'SET_REVISION_DATE'; id: string; date: Date }
  | { type: 'REMOVE_REVISION_DATE'; id: string }
  | { type: 'REMOVE_FILE'; id: string }
  | { type: 'CANCEL_ALL' }
  | { type: 'UPDATE_PROGRESS'; id: string; bytesUploaded: number }
  | { type: 'SET_ERRORS'; id: string; errors: TranslatedError<ApiRequestPath>[] }
  | { type: 'SET_UPLOAD_ID'; id: string; uploadId: string }
  | { type: 'SET_STATUS'; id: string; status: UploadItemStatus }
  | { type: 'SET_ALL_TO_PENDING' }
  | { type: 'SET_DESCRIPTION_VISIBILITY'; id: string; visible: boolean }
  | { type: 'SET_REVISION'; id: string; revision: string }
  | { type: 'SET_SCALE'; id: string; scale: string }
  | { type: 'SET_DESCRIPTION'; id: string; description: string }
  | { type: 'HIDE_DESCRIPTION'; id: string }
  | { type: 'HIDE_ALL_DESCRIPTIONS' };

type SummaryState = Summary;
type SummaryAction =
  | { type: 'SET_ALL_UPLOADED'; value: boolean }
  | { type: 'SET_ANY_FILES_IN_PROGRESS'; value: boolean }
  | { type: 'SET_NUM_ERRORS'; value: number }
  | { type: 'SET_NUM_FILES'; value: number }
  | { type: 'SET_NUM_UPLOADED'; value: number };

const tokenService = new TokenService();

export const defaultSummary = {
  anyFilesInProgress: false,
  numErrors: 0,
  numFiles: 0,
  numUploaded: 0,
};

const UploadFilesTable = (props: Props) => {
  const { t } = useTranslation();
  const { currentFolderFiles } = React.useContext(FolderListViewContext);
  const addedFilenamesInSession = React.useRef<Set<string>>(new Set());

  const [files, dispatchFiles] = React.useReducer(filesReducer, []);
  const [summary, dispatchSummary] = React.useReducer(summaryReducer, defaultSummary);

  const projectContext = React.useContext(ProjectContext);
  const { selectedFolderId } = React.useContext(TreeContext);
  const fileIdsInProgress = React.useRef<Set<string>>(new Set());

  const { onSummaryChanged: propsOnSummaryChanged } = props;

  React.useEffect(() => {
    propsOnSummaryChanged(summary);
  }, [propsOnSummaryChanged, summary]);

  React.useEffect(() => {
    dispatchSummary({ type: 'SET_NUM_FILES', value: files.length });

    dispatchSummary({
      type: 'SET_NUM_UPLOADED',
      value: files.filter((x) => x.status === UploadItemStatus.SavedToDatabase).length,
    });

    dispatchSummary({
      type: 'SET_ANY_FILES_IN_PROGRESS',
      value: files.some((x) =>
        [UploadItemStatus.UploadingToS3, UploadItemStatus.SavingToDatabase].includes(x.status),
      ),
    });

    dispatchSummary({
      type: 'SET_NUM_ERRORS',
      value: files.filter((x) => x.status === UploadItemStatus.Error).length,
    });
  }, [files, summary]);

  React.useEffect(() => {
    dispatchFiles({
      type: 'ADD_FILES',
      files: props.addFiles,
    });
  }, [props.addFiles]);

  React.useEffect(() => {
    if (props.cancelAllNonce > USE_NONCE_START_VALUE) {
      dispatchFiles({
        type: 'CANCEL_ALL',
      });
    }
  }, [props.cancelAllNonce]);

  /**
   * Returns the last file version for specified filename for files that existed before this
   * dialog was opened.
   * @param filename Name of file.
   */
  function findLastFileVersion(filename: string): FileVersionModel | undefined {
    const currentFolderFile = currentFolderFiles.find(
      (x) => x.name.toLowerCase() === filename.toLowerCase(),
    );
    return currentFolderFile ? fileVersionUtils.findLastFileVersion(currentFolderFile) : undefined;
  }

  /**
   * Returns a value indicating whether specified filename has been uploaded in the current
   * session of the dialog.
   * @param filename Name of file.
   */
  function uploadedInDialogSession(filename: string): boolean {
    return addedFilenamesInSession.current.has(filename.toLowerCase());
  }

  /**
   * Saves upload item to data base by calling the API.
   */
  const saveToDb = React.useCallback(
    async (uploadItem: UploadItem, uploadId: string) => {
      dispatchFiles({
        type: 'SET_STATUS',
        id: uploadItem.id,
        status: UploadItemStatus.SavingToDatabase,
      });

      if (selectedFolderId) {
        await SkyMapAxiosServiceFactory.instance.createFolderServiceV1().createSubFolderFile({
          path: {
            folderId: selectedFolderId,
          },
          body: {
            uploadId,
            revisionDate:
              uploadItem.meta.revisionDate && format(uploadItem.meta.revisionDate, 'yyyy-MM-dd'),
            revision: uploadItem.meta.revision,
            scale: uploadItem.meta.scale,
            description: uploadItem.meta.description,
          },
        });
      } else {
        await SkyMapAxiosServiceFactory.instance.createProjectServiceV1().createRootFolderFile({
          path: {
            projectId: projectContext.project.id,
          },
          body: {
            uploadId,
            revisionDate:
              uploadItem.meta.revisionDate && format(uploadItem.meta.revisionDate, 'yyyy-MM-dd'),
            revision: uploadItem.meta.revision,
            scale: uploadItem.meta.scale,
            description: uploadItem.meta.description,
          },
        });
      }

      addedFilenamesInSession.current.add(uploadItem.file.htmlInputFile.name.toLowerCase());

      dispatchFiles({
        type: 'SET_STATUS',
        id: uploadItem.id,
        status: UploadItemStatus.SavedToDatabase,
      });
    },
    [projectContext, selectedFolderId],
  );

  const uploadToS3 = React.useCallback(async (uploadItem: UploadItem) => {
    dispatchFiles({
      type: 'SET_STATUS',
      id: uploadItem.id,
      status: UploadItemStatus.UploadingToS3,
    });

    // Handle upload progress.
    uploadItem.file.uploadInstance?.onProgress((bytesUploaded: number, totalBytes: number) => {
      dispatchFiles({
        type: 'UPDATE_PROGRESS',
        id: uploadItem.id,
        bytesUploaded: bytesUploaded,
      });
    });

    // Start upload.
    const result = await uploadItem.file.uploadInstance?.start();
    const uploadId = result.id;

    dispatchFiles({
      type: 'SET_UPLOAD_ID',
      id: uploadItem.id,
      uploadId: uploadId,
    });

    return uploadId;
  }, []);

  const promiseGenerator = React.useCallback(() => {
    const findPredicate = (item: UploadItem) => {
      // Skip files that has already been processed/are processing.
      if (fileIdsInProgress.current.has(item.id)) {
        return false;
      }

      return item.status === UploadItemStatus.Pending;
    };

    // Get next file to upload.
    const uploadItem = files.find(findPredicate);

    // No more files?
    if (!uploadItem) {
      return null;
    }

    // Add current file to list of files currently being processed.
    // This list is used to determing the next item to process.
    // Needed in order to get next item before next render has occurred (when item status is updated).
    fileIdsInProgress.current.add(uploadItem.id);

    return new Promise<void>((resolve) => {
      iiafe(async () => {
        try {
          let uploadId = uploadItem.file.uploadId;

          // If file has already been uploaded to S3, don't upload it again.
          if (uploadId) {
            dispatchFiles({
              type: 'SET_STATUS',
              id: uploadItem.id,
              status: UploadItemStatus.SavingToDatabase,
            });
          } else {
            uploadId = await uploadToS3(uploadItem);
          }

          // Finally call SkyMap API to save file.
          await saveToDb(uploadItem, uploadId);
        } catch (err) {
          fileIdsInProgress.current.delete(uploadItem.id);

          if (!FileUpload.isManuallyStoppedError(err)) {
            handleError<ApiRequestPath>(
              err,
              ['body.revision', 'body.scale', 'body.description'],
              (errors: TranslatedError<ApiRequestPath>[]) => {
                dispatchFiles({
                  type: 'SET_ERRORS',
                  id: uploadItem.id,
                  errors,
                });
              },
            );
          }

          dispatchFiles({
            type: 'SET_STATUS',
            id: uploadItem.id,
            status: UploadItemStatus.Error,
          });
        } finally {
          // Always resolve promise, whether it was successfully uploaded or not.
          resolve();
        }
      });
    });
  }, [files, saveToDb, uploadToS3]);

  const { start: startPromisePool } = usePromisePool(3, promiseGenerator);

  const startUpload = React.useCallback(
    (itemId?: string) => {
      if (itemId) {
        dispatchFiles({
          type: 'SET_STATUS',
          id: itemId,
          status: UploadItemStatus.Pending,
        });
        dispatchFiles({
          type: 'HIDE_ALL_DESCRIPTIONS',
        });
      } else {
        dispatchFiles({
          type: 'SET_ALL_TO_PENDING',
        });
        dispatchFiles({
          type: 'HIDE_ALL_DESCRIPTIONS',
        });
      }

      // Start promise pool.
      startPromisePool();
    },
    [startPromisePool],
  );

  React.useEffect(() => {
    if (props.startUploadNonce > USE_NONCE_START_VALUE) {
      startUpload();
    }
  }, [props.startUploadNonce, startUpload]);

  function summaryReducer(state: SummaryState, action: SummaryAction): SummaryState {
    switch (action.type) {
      case 'SET_ANY_FILES_IN_PROGRESS': {
        if (state.anyFilesInProgress !== action.value) {
          return {
            ...state,
            anyFilesInProgress: action.value,
          };
        }
        break;
      }

      case 'SET_NUM_ERRORS': {
        if (state.numErrors !== action.value) {
          return {
            ...state,
            numErrors: action.value,
          };
        }
        break;
      }

      case 'SET_NUM_FILES': {
        if (state.numFiles !== action.value) {
          return {
            ...state,
            numFiles: action.value,
          };
        }
        break;
      }

      case 'SET_NUM_UPLOADED': {
        if (state.numUploaded !== action.value) {
          return {
            ...state,
            numUploaded: action.value,
          };
        }
        break;
      }
    }

    return state;
  }

  function filesReducer(state: UploadItemState, action: UploadItemAction): UploadItemState {
    switch (action.type) {
      case 'ADD_FILES': {
        // Keep only files with status Idle.
        // Files can be added only when all files has been uploaded and no errors exist, so
        // excluding other files won't be a problem.
        const list = [...state].filter((x) => x.status === UploadItemStatus.Idle);

        for (const file of action.files) {
          const existingFileLastVersion = findLastFileVersion(file.name);

          const item: UploadItem = {
            id: v4(),
            status: UploadItemStatus.Idle,
            showDescription: false,
            file: {
              isPreviouslyUploaded:
                existingFileLastVersion !== undefined || uploadedInDialogSession(file.name),
              htmlInputFile: file,
              bytesUploaded: 0,
              uploadInstance: new FileUpload({
                apiBaseUrl: getWebAPI().getURL(),
                authToken: tokenService.get(),
                file,
                concurrency: 3,
              }),
            },
            meta: {
              revision: existingFileLastVersion
                ? getNextFileVersionRevision(existingFileLastVersion.revision)
                : undefined,
            },
          };

          const index = list.findIndex(
            (x) => x.file.htmlInputFile.name === item.file.htmlInputFile.name,
          );
          if (index === -1) {
            list.push(item);
          }
        }

        list.sort((a, b) => a.file.htmlInputFile.name.localeCompare(b.file.htmlInputFile.name));

        return list;
      }

      case 'SET_REVISION_DATE': {
        const list = [...state];

        const index = list.findIndex((x) => x.id === action.id);
        list[index] = {
          ...list[index],
          meta: {
            ...list[index].meta,
            revisionDate: action.date,
          },
        };

        return list;
      }
      case 'REMOVE_REVISION_DATE': {
        const list = [...state];

        const index = list.findIndex((x) => x.id === action.id);
        list[index] = {
          ...list[index],
          meta: {
            ...list[index].meta,
            revisionDate: undefined,
          },
        };

        return list;
      }

      case 'REMOVE_FILE': {
        const item = state.find((x) => x.id === action.id);
        item?.file.uploadInstance?.stop();

        return state.filter((x) => x.id !== action.id);
      }

      case 'CANCEL_ALL': {
        const list = [...state];

        for (const item of list) {
          if (item.status === UploadItemStatus.UploadingToS3) {
            item.file.uploadInstance?.stop();
          }
        }

        for (let i = 0; i < list.length; i++) {
          if (
            ![UploadItemStatus.SavingToDatabase, UploadItemStatus.SavedToDatabase].includes(
              list[i].status,
            )
          ) {
            list[i] = {
              ...list[i],
              status: UploadItemStatus.Idle,
              file: {
                ...list[i].file,
                bytesUploaded: 0,
                uploadId: undefined,
              },
            };
          }
        }

        return list;
      }

      case 'UPDATE_PROGRESS': {
        const list = [...state];

        const index = list.findIndex((x) => x.id === action.id);
        list[index] = {
          ...list[index],
          file: {
            ...list[index].file,
            bytesUploaded: action.bytesUploaded,
          },
        };

        return list;
      }

      case 'SET_ERRORS': {
        const list = [...state];

        const index = list.findIndex((x) => x.id === action.id);
        list[index] = {
          ...list[index],
          errors: action.errors,
          showDescription: action.errors.some((x) => x.path === 'body.description'),
        };

        return list;
      }

      case 'SET_UPLOAD_ID': {
        const list = [...state];

        const index = list.findIndex((x) => x.id === action.id);
        list[index] = {
          ...list[index],
          file: {
            ...list[index].file,
            uploadId: action.uploadId,
          },
        };

        return list;
      }

      case 'SET_STATUS': {
        const list = [...state];

        const canTransitionToStatus = (item: UploadItem) => {
          switch (action.status) {
            case UploadItemStatus.Error:
              return [UploadItemStatus.UploadingToS3, UploadItemStatus.SavingToDatabase].includes(
                item.status,
              );
            default:
              return true;
          }
        };

        const index = list.findIndex((x) => x.id === action.id);

        // File might have been removed from list.
        if (index === -1) {
          return state;
        }

        if (canTransitionToStatus(list[index])) {
          list[index] = {
            ...list[index],
            status: action.status,
          };

          if (!list[index].file.uploadId) {
            list[index] = {
              ...list[index],
              file: {
                ...list[index].file,
                bytesUploaded: 0,
              },
            };
          }
        }

        // Sort order for all status enum values.
        const sortOrder: Record<UploadItemStatus, number> = {
          // Same sort order for UploadingToS3 and SavingToDatabase.
          [UploadItemStatus.UploadingToS3]: 0,
          [UploadItemStatus.SavingToDatabase]: 0,
          [UploadItemStatus.Error]: 1,
          // Idle currently not used after upload has started, but needs to be in the array
          // for typing purposes. All Idle is set to Pending when upload starts.
          //
          // However, Idle can be used if we decide to allow user to continue to add files while
          // an upload session is in progress. Then these files is editable (Idle) until they gets
          // added to the current upload session (or a new starts if all other are completed).
          [UploadItemStatus.Idle]: 2,
          [UploadItemStatus.Pending]: 2,
          [UploadItemStatus.SavedToDatabase]: 3,
        };

        return list.sort(
          (a, b) =>
            sortOrder[a.status] - sortOrder[b.status] ||
            a.file.htmlInputFile.name.localeCompare(b.file.htmlInputFile.name),
        );
      }

      case 'SET_ALL_TO_PENDING': {
        const list = [...state];

        for (let i = 0; i < list.length; i++) {
          if (list[i].status !== UploadItemStatus.SavedToDatabase) {
            list[i] = {
              ...list[i],
              status: UploadItemStatus.Pending,
            };
          }
        }
        return list;
      }

      case 'SET_DESCRIPTION_VISIBILITY': {
        const list = [...state];

        const index = list.findIndex((x) => x.id === action.id);
        list[index] = {
          ...list[index],
          showDescription: action.visible,
        };

        return list;
      }

      case 'SET_REVISION': {
        const list = [...state];

        const index = list.findIndex((x) => x.id === action.id);
        list[index] = {
          ...list[index],
          errors: list[index].errors?.filter((error) => error.path !== 'body.revision'),
          meta: {
            ...list[index].meta,
            revision: action.revision || undefined,
          },
        };

        return list;
      }

      case 'SET_SCALE': {
        const list = [...state];

        const index = list.findIndex((x) => x.id === action.id);
        list[index] = {
          ...list[index],
          errors: list[index].errors?.filter((error) => error.path !== 'body.scale'),
          meta: {
            ...list[index].meta,
            scale: action.scale || undefined,
          },
        };

        return list;
      }

      case 'SET_DESCRIPTION': {
        const list = [...state];

        const index = list.findIndex((x) => x.id === action.id);
        list[index] = {
          ...list[index],
          errors: list[index].errors?.filter((error) => error.path !== 'body.description'),
          meta: {
            ...list[index].meta,
            description: action.description || undefined,
          },
        };

        return list;
      }

      case 'HIDE_DESCRIPTION': {
        const list = [...state];

        const index = list.findIndex((x) => x.id === action.id);
        list[index] = {
          ...list[index],
          errors: list[index].errors?.filter((error) => error.path !== 'body.description'),
          showDescription: false,
        };

        return list;
      }

      case 'HIDE_ALL_DESCRIPTIONS': {
        const list = [...state];

        for (let i = 0; i < list.length; i++) {
          list[i] = {
            ...list[i],
            showDescription: false,
          };
        }

        return list;
      }
    }
  }

  const onRevisionDateSet = React.useCallback((itemId: string, date: Date) => {
    dispatchFiles({
      type: 'SET_REVISION_DATE',
      id: itemId,
      date,
    });
  }, []);

  const onRevisionDateRemoved = React.useCallback((itemId: string) => {
    dispatchFiles({
      type: 'REMOVE_REVISION_DATE',
      id: itemId,
    });
  }, []);

  const onRemoveFile = React.useCallback((itemId: string) => {
    dispatchFiles({
      type: 'REMOVE_FILE',
      id: itemId,
    });
  }, []);

  const onRetry = React.useCallback(
    (itemId: string) => {
      startUpload(itemId);
    },
    [startUpload],
  );

  const onDescriptionVisibilityChanged = React.useCallback((itemId: string, visible: boolean) => {
    dispatchFiles({
      type: 'SET_DESCRIPTION_VISIBILITY',
      id: itemId,
      visible,
    });
  }, []);

  const onRevisionChanged = React.useCallback((itemId: string, revision: string) => {
    dispatchFiles({
      type: 'SET_REVISION',
      id: itemId,
      revision: revision,
    });
  }, []);

  const onScaleChanged = React.useCallback((itemId: string, scale: string) => {
    dispatchFiles({
      type: 'SET_SCALE',
      id: itemId,
      scale: scale,
    });
  }, []);

  const onDescriptionChanged = React.useCallback((itemId: string, description: string) => {
    dispatchFiles({
      type: 'SET_DESCRIPTION',
      id: itemId,
      description,
    });
  }, []);

  // Render nothing if no files exist.
  if (files.length === 0) {
    return null;
  }

  return (
    <>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell />
            <Table.HeaderCell colSpan={2} padding={0}>
              {t('fileManager.uploadFilesDialog.columns.fileName', {
                ns: 'components',
              })}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('fileManager.uploadFilesDialog.columns.revisionDate', {
                ns: 'components',
              })}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('fileManager.uploadFilesDialog.columns.revision', {
                ns: 'components',
              })}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('fileManager.uploadFilesDialog.columns.scale', {
                ns: 'components',
              })}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('fileManager.uploadFilesDialog.columns.size', {
                ns: 'components',
              })}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('fileManager.uploadFilesDialog.columns.status', {
                ns: 'components',
              })}
            </Table.HeaderCell>
            <Table.HeaderCell />
          </Table.Row>
        </Table.Head>

        <Table.Body>
          {files.map((x) => (
            <UploadFileRow
              bytesUploaded={x.file?.bytesUploaded}
              item={x}
              key={x.id}
              onDescriptionChanged={onDescriptionChanged}
              onDescriptionVisibilityChanged={onDescriptionVisibilityChanged}
              onRemoveFile={onRemoveFile}
              onRetry={onRetry}
              onRevisionChanged={onRevisionChanged}
              onRevisionDateRemoved={onRevisionDateRemoved}
              onRevisionDateSet={onRevisionDateSet}
              onScaleChanged={onScaleChanged}
            />
          ))}
        </Table.Body>
      </Table>
    </>
  );
};

export { UploadFilesTable };
